import { Pause, Play, RotateCcw, RotateCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { computePeaks, cssVar, formatTime, getAudioContextCtor, getDecodeContext } from './waveform-utils';

import { Button } from '@/components/ui/button';
import SelectMenu from '@/components/ui/select-menu';
import { playbackSpeedOptions } from '@/lib/playback-speed';

const SKIP_SECONDS = 10;
const SEEK_STEP_SECONDS = 5;
const SEEK_STEP_SECONDS_LARGE = 15;
// Local-only, per-browser convenience (not synced anywhere) — resumes a clip where a teacher left
// off listening, keyed by asset id so it survives a page reload but not a different device/browser.
const PLAYHEAD_STORAGE_PREFIX = 'agavi:sound-playhead:';

function extractAssetId(url: string): string | null {
  const m = url.match(/\/assets\/([^/?]+)\/file/);
  return m?.[1] ?? null;
}

function readSavedPlayhead(assetId: string | null): number {
  if (!assetId) return 0;
  try {
    const raw = localStorage.getItem(PLAYHEAD_STORAGE_PREFIX + assetId);
    const value = raw ? Number(raw) : 0;
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function savePlayhead(assetId: string | null, seconds: number) {
  if (!assetId) return;
  try {
    localStorage.setItem(PLAYHEAD_STORAGE_PREFIX + assetId, String(seconds));
  } catch {
    /* localStorage unavailable (e.g. private mode) — resuming position just won't persist */
  }
}

// A plain RotateCcw/RotateCw reads as generic "undo/redo"; the seconds count centered inside the
// ring is what makes it read as "skip back/forward N seconds" (the familiar podcast-app
// convention), rather than lucide's SkipBack/SkipForward icons, which look like previous/next-
// track controls. Sized deliberately large — at the original small size the number collided with
// the icon's own arrow strokes; a bigger ring leaves the number room to sit cleanly inside it.
function SkipGlyph({ direction }: { direction: 'back' | 'forward' }) {
  const Icon = direction === 'back' ? RotateCcw : RotateCw;
  return (
    <span className='relative flex h-6 w-6 items-center justify-center'>
      <Icon className='h-6 w-6' strokeWidth={1.5} />
      <span className='absolute inset-0 flex items-center justify-center pt-px text-[8px] font-bold leading-none'>{SKIP_SECONDS}</span>
    </span>
  );
}

export interface WaveformPlayerProps {
  src?: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
  height?: number;
  // Fixed pixel width for the waveform when it sits beside the Play button (hideSkip mode) rather
  // than on its own full-width row — that layout has no other sibling content to derive an
  // implicit width from, so a definite width is required or the flex item collapses to 0.
  width?: number;
  showSpeed?: boolean;
  // Hides the skip-back/skip-forward buttons — for a context that only wants "play + waveform"
  // and nothing else (MCQ's media options: a compact selectable tile has no room for, and no real
  // need for, precise seeking). Row 1's skip buttons are otherwise unconditional even when
  // `interactive` is false, since they're a discrete precise action, not a scrub gesture. Also
  // switches the layout to a single row (play beside the waveform) with no time readout.
  hideSkip?: boolean;
  // Only meaningful alongside `hideSkip` — drops that mode's own `p-3 bg-ws-btn rounded-lg` outer
  // box (redundant chrome when the CALLER already provides its own bordered/backgrounded
  // container, e.g. Ordering's item tiles/rows) and shrinks the play button, freeing up space for
  // genuinely tight layouts. Added for Ordering's circular ring tiles specifically — a fixed-width
  // tile plus the ordinary hideSkip wrapper's own padding needed ~190px for audio alone, blowing
  // out a 108-140px tile — but applied to every `constrained` media context (including horizontal
  // shape's rows), not gated narrowly to circular, since the same "the caller already has its own
  // box" reasoning applies there too.
  compact?: boolean;
  // false in the Sound block's collapsed card only — that card's own click handler opens the
  // editor, so the waveform there is a ghosted, non-scrubbable glance; only Play stays live.
  interactive?: boolean;
  // Restarts from the beginning at natural end instead of stopping — a per-placement setting
  // (SoundBlockData.loop), not something this component decides on its own.
  loop?: boolean;
  /**
   * Whose clip this is, when the player sits inside something that has its own identity.
   *
   * An MCQ or Ordering item embeds one of these per option, and every embedded Play button was
   * named just "Play" — so a student tabbing five audio options heard "Play, button" five times
   * with nothing saying which clip each one would play. The owner passes its own name in and the
   * button becomes "Play sound clip 3".
   *
   * Undefined for a player that IS the content (the Sound block, the library preview): there the
   * bare verb is right, because there is only one clip and nothing to distinguish it from.
   */
  ownerName?: string;
  // Called when the teacher presses "Try again" after a load failure — the signed URL this
  // component was given may have simply expired, so a real recovery needs the PARENT to re-fetch
  // the asset and pass down a freshly-signed src; this component's own retry (re-fetching the same
  // src) only helps with a transient network blip, not an actually-expired signature.
  onRetry?: () => void;
}

// Read-only counterpart to AudioTrimmer — same waveform-drawing approach, but for a clip that's
// already been trimmed/faded server-side (so there's nothing to edit, just play back). Used
// everywhere a placed/saved clip is shown: the block's collapsed card, the student-facing preview,
// the open editor, and the library picker's detail panel.
/**
 * The height this player occupies, for a caller that has to hold its place before a clip arrives.
 *
 * Here, next to the layout it describes, because it is arithmetic over that layout and a copy of
 * it anywhere else would go quietly wrong the first time a row changed. Each term names the class
 * it comes from, so changing one is a visible prompt to change the other:
 *
 *   padding   24  `p-3` on the root, top and bottom
 *   controls  36  row 1, whose tallest child is the play button (`h-9`, or `h-6` compact)
 *   gap        8  `gap-2` between rows
 *   waveform       the `height` prop, applied as an inline style
 *   speed     34  row 3's select trigger: 1px border + `py-1.5` + `text-sm`'s 20px line, doubled
 *
 * Verified against the app's own CSS rather than taken on trust: the collapsed card's player
 * (height 40) measures 150px, with rows of 36 / 40 / 34.
 *
 * `showSpeed` defaults to ON, which is easy to miss — the Sound block's collapsed card does not
 * pass it, so it has a third row and is 34px taller than a reading of the call site suggests.
 */
export function playerBoxHeight(
  { height = 64, showSpeed = true, compact = false }: { height?: number; showSpeed?: boolean; compact?: boolean } = {},
): number {
  const PADDING = 24;
  const GAP = 8;
  const CONTROLS = compact ? 24 : 36;
  const SPEED_ROW = 34;
  return PADDING + CONTROLS + GAP + height + (showSpeed ? GAP + SPEED_ROW : 0);
}

export function WaveformPlayer({ src, t, height = 64, width, showSpeed = true, hideSkip = false, compact = false, interactive = true, loop = false, ownerName, onRetry }: WaveformPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  const assetIdRef = useRef<string | null>(null);
  const lastPersistedSecondRef = useRef<number | null>(null);
  const loopRef = useRef(loop);
  loopRef.current = loop;

  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [preparing, setPreparing] = useState(false); // fetching+decoding the full clip for a first play
  const [retryTick, setRetryTick] = useState(0);
  const [peaks, setPeaks] = useState<{ min: Float32Array; max: Float32Array } | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const [rate, setRate] = useState(1);

  // Free the PLAYBACK context + decoded buffer (created lazily on first play). The waveform peaks
  // survive — they're tiny — so an idle player keeps drawing without holding tens of MB of PCM.
  function releasePlayback() {
    stopPlayback();
    if (audioContextRef.current) { void audioContextRef.current.close(); audioContextRef.current = null; }
    audioBufferRef.current = null;
  }

  // On mount / src change: fetch + decode ONCE, just to extract the waveform peaks + duration, then
  // let the decoded buffer be GC'd. We deliberately do NOT keep the AudioBuffer or open a per-player
  // AudioContext here — a mounted-but-never-played tile (collapsed card, preview, library list) must
  // not sit on tens of MB of decoded audio. The full buffer + a real playback context are created
  // lazily in togglePlayPause the first time the teacher actually presses Play.
  useEffect(() => {
    if (!src) { setReady(false); setPeaks(null); return; }
    let cancelled = false;
    setReady(false);
    setLoadError(false);
    setPlaying(false);
    setPeaks(null);
    setDuration(0);
    releasePlayback();
    assetIdRef.current = extractAssetId(src);
    setPlayhead(readSavedPlayhead(assetIdRef.current));
    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      // Shared, decode-only context (see waveform-utils) — never played through, so one is enough
      // for the whole app no matter how many tiles are on screen.
      .then((buf) => getDecodeContext().decodeAudioData(buf))
      .then((buffer) => {
        if (cancelled) return;
        setDuration(buffer.duration);
        setPlayhead((p) => Math.min(p, buffer.duration));
        // Match the old width-scaled resolution using the element's real width (layout has settled by
        // the time this async decode resolves); computed once, then the buffer is released.
        const w = containerRef.current?.clientWidth || 600;
        setPeaks(computePeaks(buffer, Math.min(1000, Math.max(60, Math.floor(w / 2)))));
        setReady(true);
      })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; releasePlayback(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, retryTick]);

  // Best-effort — if the tab closes or the block editor unmounts mid-playback, persist wherever
  // the playhead last was so the periodic in-tick save below isn't the only chance to capture it.
  useEffect(() => () => { savePlayhead(assetIdRef.current, playhead); }, [playhead]);

  useEffect(() => {
    const el = containerRef.current;
    // `src` starts empty (the caller fetches the asset's signed URL asynchronously), so the
    // container div this observes doesn't exist yet on first mount — this effect must re-run
    // once `src` arrives and the real element is committed, or the observer is permanently
    // attached to nothing and the waveform never draws.
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [src]);

  useEffect(() => {
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peaks, containerWidth, playhead, height]);

  function timeToX(time: number): number {
    if (duration <= 0) return 0;
    return (time / duration) * containerWidth;
  }

  function xToTime(clientX: number): number {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return frac * duration;
  }

  function draw() {
    const canvas = canvasRef.current;
    if (!canvas || !peaks || !containerWidth) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = containerWidth * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, containerWidth, height);

    const muted = cssVar(canvas, '--color-ws-muted', '#94a3b8');
    const primary = cssVar(canvas, '--color-ws-primary', '#2563eb');
    const mid = height / 2;

    ctx.fillStyle = muted;
    const barWidth = containerWidth / peaks.min.length;
    for (let i = 0; i < peaks.min.length; i++) {
      const x = i * barWidth;
      const yTop = mid - peaks.max[i]! * mid;
      const yBottom = mid - peaks.min[i]! * mid;
      ctx.fillRect(x, yTop, Math.max(1, barWidth - 1), Math.max(1, yBottom - yTop));
    }

    ctx.strokeStyle = primary;
    ctx.lineWidth = 2;
    const playheadX = timeToX(playhead);
    ctx.beginPath();
    ctx.moveTo(playheadX, 0);
    ctx.lineTo(playheadX, height);
    ctx.stroke();
  }

  function stopPlayback() {
    if (sourceNodeRef.current) {
      sourceNodeRef.current.onended = null;
      try { sourceNodeRef.current.stop(); } catch { /* already stopped */ }
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }

  function playFrom(startAt: number, rateToUse = rate) {
    const ctx = audioContextRef.current;
    const buffer = audioBufferRef.current;
    if (!ctx || !buffer) return;
    stopPlayback();
    if (ctx.state === 'suspended') void ctx.resume();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = rateToUse;
    source.connect(ctx.destination);
    const now = ctx.currentTime;
    source.start(0, startAt);
    sourceNodeRef.current = source;
    setPlaying(true);
    lastPersistedSecondRef.current = null;
    source.onended = () => {
      if (loopRef.current) { playFrom(0, rateToUse); return; }
      setPlaying(false);
      setPlayhead(0);
      savePlayhead(assetIdRef.current, 0);
    };
    const tick = () => {
      if (!audioContextRef.current) return;
      const elapsed = (audioContextRef.current.currentTime - now) * rateToUse;
      const t = Math.min(buffer.duration, startAt + elapsed);
      setPlayhead(t);
      const wholeSecond = Math.floor(t);
      if (wholeSecond !== lastPersistedSecondRef.current) {
        lastPersistedSecondRef.current = wholeSecond;
        savePlayhead(assetIdRef.current, t);
      }
      if (t < buffer.duration) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  function togglePlayPause() {
    if (playing) {
      stopPlayback();
      setPlaying(false);
      savePlayhead(assetIdRef.current, playhead);
      return;
    }
    const startAt = Math.min(playhead, Math.max(0, duration - 0.02));
    // Already loaded for playback (a prior play this session) → start immediately.
    if (audioContextRef.current && audioBufferRef.current) { playFrom(startAt); return; }
    // First play: create + resume the real playback context NOW, synchronously inside this click, so
    // the browser's autoplay policy lets it produce sound; then fetch + decode the clip lazily. This
    // is the only point a WaveformPlayer ever holds decoded PCM — and only for a clip being played.
    if (!src || preparing) return;
    const ctx = audioContextRef.current ?? new (getAudioContextCtor())();
    audioContextRef.current = ctx;
    void ctx.resume();
    setPreparing(true);
    fetch(src)
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.arrayBuffer(); })
      .then((buf) => ctx.decodeAudioData(buf))
      .then((buffer) => {
        audioBufferRef.current = buffer;
        setPreparing(false);
        playFrom(Math.min(startAt, Math.max(0, buffer.duration - 0.02)));
      })
      .catch(() => { setPreparing(false); setLoadError(true); });
  }

  function seekTo(target: number) {
    const clamped = Math.min(duration, Math.max(0, target));
    if (playing) { stopPlayback(); setPlaying(false); }
    setPlayhead(clamped);
    savePlayhead(assetIdRef.current, clamped);
  }

  function skip(delta: number) {
    seekTo(playhead + delta);
  }

  function handleRateChange(next: string) {
    const value = Number(next);
    setRate(value);
    if (playing) playFrom(playhead, value);
  }

  function handleContainerPointerDown(e: React.PointerEvent) {
    // Ghosted/non-interactive mode: let the click through to whatever's underneath (the Sound
    // block's collapsed card opens its editor) instead of capturing it as a scrub gesture.
    if (!interactive) return;
    // Otherwise this component sometimes sits inside a card whose own click handler opens an
    // editor — stop propagation so operating the player doesn't also fire that outer handler.
    e.stopPropagation();
    if (!ready) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    if (playing) { stopPlayback(); setPlaying(false); }
    draggingRef.current = true;
    setPlayhead(xToTime(e.clientX));
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!draggingRef.current) return;
    setPlayhead(xToTime(e.clientX));
  }

  function handlePointerUp() {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    savePlayhead(assetIdRef.current, playhead);
  }

  function handleScrubKeyDown(e: React.KeyboardEvent) {
    if (!ready) return;
    const step = e.shiftKey ? SEEK_STEP_SECONDS_LARGE : SEEK_STEP_SECONDS;
    if (e.key === 'ArrowLeft') { e.preventDefault(); seekTo(playhead - step); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); seekTo(playhead + step); }
    else if (e.key === 'Home') { e.preventDefault(); seekTo(0); }
    else if (e.key === 'End') { e.preventDefault(); seekTo(duration); }
  }

  function handleRetry() {
    setRetryTick((n) => n + 1);
    onRetry?.();
  }

  if (!src) return <p className='text-sm italic text-ws-muted'>{t('blocks.sound.loading')}</p>;

  const playButton = (
    <button
      type='button'
      onClick={interactive ? togglePlayPause : (e) => { e.stopPropagation(); togglePlayPause(); }}
      onKeyDown={(e) => e.stopPropagation()}
      disabled={!ready || preparing}
      aria-label={ownerName
        ? t(playing ? 'blocks.sound.pauseOwned' : 'blocks.sound.playOwned', { name: ownerName })
        : t(playing ? 'blocks.sound.pause' : 'blocks.sound.play')}
      className={`flex shrink-0 items-center justify-center rounded-full bg-ws-primary text-ws-btn-on-primary hover:opacity-90 disabled:opacity-50 ${compact ? 'h-6 w-6' : 'h-9 w-9'}`}
    >
      {preparing
        ? <RotateCw className={`animate-spin ${compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'}`} />
        : playing
        ? <Pause className={compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} />
        : <Play className={`translate-x-[1px] ${compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'}`} />}
    </button>
  );

  const waveform = (
    <div
      ref={containerRef}
      role={interactive ? 'slider' : undefined}
      aria-label={interactive ? t('blocks.sound.scrub') : undefined}
      aria-valuemin={interactive ? 0 : undefined}
      aria-valuemax={interactive ? duration : undefined}
      aria-valuenow={interactive ? playhead : undefined}
      aria-valuetext={interactive ? formatTime(playhead) : undefined}
      tabIndex={interactive ? 0 : -1}
      onKeyDown={interactive ? handleScrubKeyDown : undefined}
      className={`relative shrink-0 focus-visible:[outline:2px_auto] ${width == null ? 'w-full' : ''} ${interactive ? 'touch-none select-none' : 'opacity-40'}`}
      style={{ height, width }}
      onPointerDown={handleContainerPointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <canvas ref={canvasRef} aria-hidden='true' className='absolute inset-0 h-full w-full rounded-md' />
      {!ready && !loadError && (
        <div className='absolute inset-0 flex items-center justify-center'>
          <p className='text-xs italic text-ws-muted'>{t('blocks.sound.loadingWaveform')}</p>
        </div>
      )}
      {loadError && (
        <div className='absolute inset-0 flex flex-col items-center justify-center gap-1.5 rounded-md bg-ws-btn'>
          <p role='alert' className='text-xs text-ws-warning-fg'>{t('blocks.sound.loadError')}</p>
          {/* The shared primitive, like the one on `MediaPlaceholder`: this was the hand-rolled
              button the placeholder's was first copied from, and two spellings of one control in
              one flow is how the button drift this component library exists to stop begins. */}
          <Button size='sm' icon={<RotateCw />} onClick={(e) => { e.stopPropagation(); handleRetry(); }}>
            {t('blocks.sound.tryAgain')}
          </Button>
        </div>
      )}
    </div>
  );

  // hideSkip doubles as "simple player" mode (MCQ's compact selectable option tile) — no skip/
  // time controls earn their space there, so play sits directly beside the waveform in one row
  // instead of the full player's stacked rows. `compact` drops that row's own padded/backgrounded
  // box entirely — the caller already provides one (a tile's or row's own border+background) — so
  // this doesn't pay for a second layer of chrome on top of an already-tight budget.
  if (hideSkip) {
    return (
      <div
        className={compact ? 'flex items-center gap-1.5' : 'flex items-center gap-2 rounded-lg bg-ws-btn p-3'}
        onClick={interactive ? (e) => e.stopPropagation() : undefined}
      >
        {playButton}
        {waveform}
      </div>
    );
  }

  return (
    <div
      className='flex flex-col gap-2 rounded-lg bg-ws-btn p-3'
      // This component sometimes sits inside a card whose own click handler opens an editor (the
      // Sound block's collapsed view) — stop propagation at the root so operating any control here
      // (play, scrub, speed) never also fires that outer handler. In non-interactive mode there's
      // nothing to protect but the Play button itself (which stops propagation individually), so
      // every other click is left to bubble through and open that editor as normal.
      onClick={interactive ? (e) => e.stopPropagation() : undefined}
    >
      {/* Row 1 — skip back/play/skip forward + elapsed/duration, kept close together on the left
          rather than spread across the row. Skip stays a real, usable control even when the
          waveform itself is ghosted/non-scrubbable (interactive=false) — it's a precise, discrete
          button, not a drag gesture competing for the card's own hit area the way scrubbing would. */}
      <div className='flex items-center gap-2'>
        <button
          type='button'
          onClick={interactive ? () => skip(-SKIP_SECONDS) : (e) => { e.stopPropagation(); skip(-SKIP_SECONDS); }}
          // A keyboard Enter/Space activation dispatches a keydown BEFORE the synthetic click —
          // that keydown still bubbles even though the click above stops it, so a ghosted
          // collapsed card's own onKeyDown (which also treats Enter/Space as "open the editor")
          // would otherwise fire at the same time as this button's own action.
          onKeyDown={(e) => e.stopPropagation()}
          disabled={!ready}
          aria-label={t('blocks.sound.skipBack', { seconds: SKIP_SECONDS })}
          title={t('blocks.sound.skipBack', { seconds: SKIP_SECONDS })}
          className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ws-muted hover:bg-ws-hover hover:text-ws-text disabled:opacity-50'
        >
          <SkipGlyph direction='back' />
        </button>
        {playButton}
        <button
          type='button'
          onClick={interactive ? () => skip(SKIP_SECONDS) : (e) => { e.stopPropagation(); skip(SKIP_SECONDS); }}
          onKeyDown={(e) => e.stopPropagation()}
          disabled={!ready}
          aria-label={t('blocks.sound.skipForward', { seconds: SKIP_SECONDS })}
          title={t('blocks.sound.skipForward', { seconds: SKIP_SECONDS })}
          className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ws-muted hover:bg-ws-hover hover:text-ws-text disabled:opacity-50'
        >
          <SkipGlyph direction='forward' />
        </button>
        <span className='shrink-0 text-xs tabular-nums text-ws-muted'>
          {formatTime(playhead)} / {formatTime(duration)}
        </span>
      </div>

      {/* Row 2 — waveform, full width and on its own line so nothing (the play button, the time
          readout, or an overlaid swap/clear button elsewhere on the card) ever competes with it
          for horizontal space. Keyboard-focusable + arrow-key seekable when interactive, matching
          the accessibility already built into AudioTrimmer's trim/fade handles. */}
      {waveform}

      {/* Row 3 — speed. A real, discrete control even when interactive=false (the collapsed card),
          matching Row 1's skip/play buttons — stop propagation explicitly so opening the select
          doesn't also bubble up into that card's own "click to edit" handler. */}
      {showSpeed && (
        <div className='flex items-center justify-end gap-2' onClick={(e) => e.stopPropagation()}>
          {/* `aria-hidden`, and not a `<label>` with a `for`: it carries no association, so a reader
              met it as stray text and then heard "Speed" again from the select that is already
              named. The word stays on screen because the control needs one there. */}
          <span aria-hidden='true' className='text-xs text-ws-muted'>{t('blocks.sound.playbackSpeedLabel')}</span>
          <SelectMenu
            value={String(rate)}
            onChange={handleRateChange}
            options={playbackSpeedOptions()}
            onKeyDown={e => e.stopPropagation()}
            /* The SAME name DataViz's sonification speed carries. One control, two blocks, and two
               names for it was the sort of difference nobody chose. */
            ariaLabel={t('blocks.sound.playbackSpeedA11yLabel')}
            className='w-fit' />
        </div>
      )}
    </div>
  );
}
